<script>
import {Link} from "@inertiajs/vue3";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import IconHeart from "@js/components/Icons/IconHeart.vue";
import IconHeartFilled from "@js/components/Icons/IconHeartFilled.vue";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";
import {useLikeStore} from "@js/store/LikeStore.js";
import {useCompareStore} from "@js/store/CompareStore.js";
import {useCartStore} from "@js/store/CartStore.js";
import IconCheckMark from "@js/components/Icons/IconCheckMark.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import {computed} from "vue";
import CartQuantityStepper from "@js/components/Sections/Cart/Component/CartQuantityStepper.vue";
import {useCatalogStore} from "@js/store/CatalogStore.js";
import IconInfo from "@js/components/Icons/IconInfo.vue";
import {useProductStore} from "@js/store/ProductStore.js";
import {ecomClick, mapProduct} from "@js/ecommerce.js";

export default {
    name: "CatalogProductItemGrid",
    components: {
        IconInfo,
        CartQuantityStepper,
        OnlyMobile,
        OnlyDesktop,
        IconCheckMark,
        NumberFormatComponent,
        IconHeart,
        IconHeartFilled,
        ImageLoader,
        Link,
    },
    props: {
        data: {type: Object, required: true},
        // defaultMode больше не нужен логически, но оставим пропс, чтобы ничего не ломать
        defaultMode: {type: String, default: "t"},
        listName: {type: String, default: 'Каталог'},
        position: {type: Number, default: 1},
    },
    methods: {
        trackClick() {
            try {
                ecomClick(mapProduct(this.data, {list: this.listName, position: this.position}))
            } catch (e) {
            }
        }
    },
    setup(props) {
        const likeStore = useLikeStore();
        const compareStore = useCompareStore();
        const cartStore = useCartStore();
        const catalogStore = useCatalogStore();
        const productStore = useProductStore();

        // НОВЫЙ ПРОЕКТ: единица одна — тонна, цена одна — price
        cartStore.registerCatalogProduct(props.data, 't');

        const qtyProxy = computed({
            get: () => cartStore.uiOf(props.data.id).qty,
            set: (val) => cartStore.setQtyFromInput(props.data.id, val),
        });

        console.log(props.data.price_type)
        const unitText = computed(() => props.data.price_type === 'kg' ? '/кг' : '/тонна');

        const hasAnyPrice = computed(() => Number(props.data?.price ?? 0) > 0);

        const canBuy = computed(
            () => !!props.data.available && cartStore.priceActive(props.data.id) > 0
        );

        const imageSrc = computed(() => {
            // 1) если бек уже прислал готовые url
            const u = props.data?.image_urls?.[0]
            if (u) return u

            // 2) если вдруг пришли только имена (на всякий)
            const f = props.data?.images?.[0]
            if (f) return `/storage/products/${encodeURIComponent(f)}`

            // 3) заглушка
            return '/images/no-image.svg'
        })

        return {
            catalogStore,
            likeStore,
            compareStore,
            productStore,
            cartStore,
            qtyProxy,
            unitText,
            hasAnyPrice,
            canBuy,
            imageSrc
        };
    },
};
</script>

<template>
    <div
        class="relative w-full rounded-2xl border border-gray-200 bg-white overflow-hidden h-full flex flex-col gap-4">
        <!-- Картинка + бейджи -->
        <Link :href="route('product', data.slug)" @pointerdown="trackClick" class="relative w-full p-0">
            <div class="overflow-hidden w-full border-b border-gray-200">
                <ImageLoader
                    :title="data.title"
                    :src="imageSrc"
                    imgClasses="w-full h-full object-contain"
                />
            </div>
            <div class="absolute left-0 top-0 flex gap-1">
                <span v-if="data.is_promo"
                      class="rounded bg-amber-400 px-2 py-0.5 text-[11px] font-medium text-black">Акция</span>
                <span v-if="data.is_hit"
                      class="rounded bg-green-600 px-2 py-0.5 text-[11px] font-medium text-white">Хит</span>
            </div>
        </Link>

        <!-- Центр -->
        <div class="flex min-w-0 grow flex-col gap-2 py-0 p-3">
            <div class="w-min text-nowrap text-[11px] px-2 py-0.5 rounded"
                 :class="[data.available ? 'bg-emerald-800 text-white': 'bg-amber-400 text-black']">
                {{ data.available ? 'Принимаем' : 'Временно не принимаем' }}
            </div>

            <Link :href="route('product', data.slug)" @pointerdown="trackClick"
                  class="max-w-[400px] md:line-clamp-2 text-sm md:text-base font-medium leading-5 text-gray-900 hover:text-navHoverColor transition">
                {{ data.title }}
            </Link>

            <!-- Цена -->
            <div v-if="hasAnyPrice" class="flex items-center gap-2">
                <div class="text-sm text-gray-700">
                    <div class="font-medium text-xs">Цена:</div>
                    <NumberFormatComponent
                        :number="data.price"
                        append_text=" ₽"
                        class="text-sm font-semibold text-black"
                    />
                    <span class="font-medium text-sm">{{ unitText }}</span>
                </div>
            </div>
            <div v-else class="font-medium leading-tight">
                Цена договорная
            </div>
        </div>

        <!-- Низ -->
        <div class="flex flex-col items-stretch mt-auto gap-3 pt-0 w-full p-3">
            <!-- Количество (в тоннах) -->
            <div class="flex items-center gap-3" v-if="canBuy">
                <CartQuantityStepper
                    v-model="qtyProxy"
                    :min="cartStore.minQty(data.id)"
                    :step="cartStore.step(data.id)"
                    :disabled="!canBuy"
                    size="md"
                    width-class="w-32"
                    @inc="cartStore.incQty(data.id)"
                    @dec="cartStore.decQty(data.id)"
                />
                <div class="text-base font-semibold text-gray-900">
                    <NumberFormatComponent
                        :number="cartStore.totalPrice(data.id)"
                        prepend_text="Итого: "
                        append_text=" ₽"
                    />
                </div>
            </div>

            <!-- Параметры: в новом проекте mass_per_meter убрали, поэтому оставим только вес (если есть расчёт в CartStore) -->
            <div v-if="canBuy" class="mb-1 flex flex-wrap gap-2 text-[11px] font-medium text-gray-700">
                <span v-if="cartStore.weightKg(data.id)" class="text-nowrap leading-none rounded bg-grayBg px-2 py-1.5">
                    Итоговый вес:
                    <NumberFormatComponent :number="cartStore.weightKg(data.id)" append_text=" кг" :animate="false"/>
                </span>
            </div>

            <!-- CTA -->
            <template v-if="!canBuy">
                <div class="flex flex-col gap-2">
                    <button
                        @click.prevent="productStore.showFastBuyPopup(data.id)"
                        class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-amber-400 text-base font-semibold text-black transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Узнать цену
                    </button>
                </div>
            </template>
            <template v-else>
                <div class="flex gap-2">
                    <template v-if="cartStore.isInCartExact(data.id)">
                        <Link :href="route('cart')"
                              class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 text-sm font-semibold text-white transition hover:bg-emerald-500">
                            <IconCheckMark class="w-5 h-5"/>
                            Уже в расчёте
                        </Link>
                    </template>
                    <template v-else>
                        <button
                            @click="cartStore.addToCartFromCard(data.id)"
                            :disabled="!canBuy"
                            class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-baseColor text-sm font-semibold text-black transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Добавить в расчёт
                        </button>
                    </template>

                    <button @click.prevent="productStore.showFastBuyPopup(data.id)"
                            class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-black/10 border-black/10 text-sm font-semibold text-black transition md:hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50">
                        Сдать сейчас
                    </button>
                </div>
            </template>
        </div>

        <!-- Избранное/Сравнение -->
        <div class="absolute flex gap-2 end-2 top-2">
<!--            <button-->
<!--                class="group inline-flex h-9 w-9 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50"-->
<!--                @click.prevent="compareStore.toggleCompare(data.id)"-->
<!--                :aria-pressed="compareStore.isCompared(data.id)"-->
<!--                :title="compareStore.isCompared(data.id) ? 'Убрать из сравнения' : 'В сравнении'">-->
<!--                <IconScales v-if="compareStore.isCompared(data.id)"-->
<!--                            class="min-w-5 min-h-5 max-w-5 max-h-5 mb-[3px] text-blue-500"/>-->
<!--                <IconScales v-else-->
<!--                            class="min-w-5 min-h-5 max-w-5 max-h-5 mb-[3px] text-gray-700 group-hover:text-blue-500 transition"/>-->
<!--            </button>-->

            <button
                class="group inline-flex h-9 w-9 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50"
                @click.prevent="likeStore.toggleLike(data.id)"
                :aria-pressed="likeStore.isLiked(data.id)"
                :title="likeStore.isLiked(data.id) ? 'Убрать из избранного' : 'В избранное'">
                <IconHeartFilled v-if="likeStore.isLiked(data.id)" class="h-5 w-5 text-likeColor"/>
                <IconHeart v-else class="h-5 w-5 text-gray-700 group-hover:text-likeColor transition"/>
            </button>
        </div>
    </div>
</template>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 2;
}
</style>
